import React, {useState, useEffect} from 'react';
import axios from 'axios';
import { useAuth } from './contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import './ReportSourcesView.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import ReportSourceDetailView, {DocumentDetails, SearchResult} from './ReportSourceDetailView';
import Loader from './components/Loader';
import env from './utils/env';

// Define proper types
interface ReportSource {
    _id: string;
    name: string;
    description?: string;
    url?: string;
    count?: number;
    filename?: string;
    is_pdf?: boolean;
    token_size?: number;
    update_timestamp?: string;
    extracted_timestamp?: string;
    metadata?: {
        title?: string;
        author?: string;
        date?: string;
        document_type?: string;
        language?: string;
        subject?: string;
        keywords?: string[];
    };
    metadata_validation_details?: {
        title?: {
            field: string;
            value: string;
            found: boolean;
            confidence: number;
            matches?: any[];
        };
        author?: {
            field: string;
            value: string;
            found: boolean;
            confidence: number;
            matches?: any[];
        };
        date?: {
            field: string;
            value: string;
            found: boolean;
            confidence: number;
            matches?: any[];
        };
        keywords?: {
            field: string;
            value: string[];
            found: boolean;
            confidence: number;
            matches?: any[];
        };
        subject?: {
            field: string;
            value: string;
            found: boolean;
            confidence: number;
            matches?: any[];
        };
    };
    metadata_validated?: string;
}

interface PaginationMetadata {
    totalDocuments: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
}

interface ReportSourcesResponse {
    reportSources: ReportSource[];
    pagination?: PaginationMetadata;
}

const ReportSourcesView: React.FC = () => {
    const { token, authLoaded } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const apiKey = env.apiKey;
    const apiUrl = env.apiUrl;
    const [reportSources, setReportSources] = useState<ReportSource[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [searchLoading, setSearchLoading] = useState<boolean>(false);
    const [searchResults] = useState<SearchResult[]>([]);
    const [filteredSources, setFilteredSources] = useState<ReportSource[]>([]);
    const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
    const [documentText, setDocumentText] = useState<string | null>(null);
    const [documentUrl, setDocumentUrl] = useState<string | null>(null);
    const [isPdf, setIsPdf] = useState<boolean>(false);
    const [documentTitle, setDocumentTitle] = useState<string>('');
    const [loadingDocument, setLoadingDocument] = useState<boolean>(false);
    const [documentDetails, setDocumentDetails] = useState<DocumentDetails | null>(null);
    const [documentChunks, setDocumentChunks] = useState<SearchResult[]>([]);
    const [pagination, setPagination] = useState<PaginationMetadata | null>(null);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [searchInput, setSearchInput] = useState<string>('');
    const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
    const [returnTo, setReturnTo] = useState<string | null>(null);
    const limit = 100; // Number of items per page

    // Fetch report sources when dependencies change
    useEffect(() => {
        if (authLoaded && !selectedDocumentId) {
            fetchReportSources();
        }
    }, [token, authLoaded, currentPage, searchQuery, selectedDocumentId]);

    const fetchReportSources = () => {
        // Use searchLoading for searches, main loading only for initial load
        if (searchQuery || currentPage > 1) {
            setSearchLoading(true);
        } else {
            setLoading(true);
        }

        // Build query parameters
        const params = new URLSearchParams({
            limit: limit.toString(),
            page: currentPage.toString()
        });

        // Add search parameter if it exists
        if (searchQuery.trim()) {
            params.append('search', searchQuery.trim());
        }
        
        const headers: Record<string, string> = {
            'x-api-key': `${apiKey}`
        };
        
        // Add authorization header if user is authenticated
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        axios.get<ReportSourcesResponse>(`${apiUrl}/report-sources?${params.toString()}`, {
            headers
        })
            .then(response => {
                if (Array.isArray(response.data.reportSources)) {
                    const sources = response.data.reportSources;
                    setReportSources(sources);
                    setFilteredSources(sources);
                } else {
                    console.error('Report sources is not an array:', response.data.reportSources);
                    setReportSources([]);
                    setFilteredSources([]);
                }

                // Set pagination metadata if available
                if (response.data.pagination) {
                    setPagination(response.data.pagination);
                }

                setLoading(false);
                setSearchLoading(false);
            })
            .catch(error => {
                console.error('Error fetching report sources:', error);
                setLoading(false);
                setSearchLoading(false);
            });
    };

    // Handle page change
    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };

    // Handle search changes with debouncing
    const handleSearchChange = (value: string) => {
        setSearchInput(value); // Update input state immediately

        // Clear existing timeout
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }

        // Set new timeout for debouncing
        const newTimeout = setTimeout(() => {
            setSearchQuery(value);
            setCurrentPage(1); // Reset to first page when searching
        }, 800); // 800ms delay

        setSearchTimeout(newTimeout);
    };

    // Handle clearing the search
    const handleClearSearch = () => {
        setSearchQuery('');
        setSearchInput(''); // Clear input state as well
        setCurrentPage(1);
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }
    };

    // Handle clicking on a search result to view document
    const handleViewDocument = (documentId: string) => {
        setLoadingDocument(true);
        setSelectedDocumentId(documentId);
        setDocumentText(null);
        setDocumentUrl(null);

        // Find document source details
        const source = reportSources.find(source => source._id === documentId);
        setDocumentTitle(source?.name || 'Document');

        // Find all chunks related to this document
        const relatedChunks = searchResults.filter(
            result => result.entity.document_id === documentId
        );
        setDocumentChunks(relatedChunks);

        const documentHeaders: Record<string, string> = {
            'x-api-key': `${apiKey}`
        };
        
        // Add authorization header if user is authenticated
        if (token) {
            documentHeaders['Authorization'] = `Bearer ${token}`;
        }

        axios.get<DocumentDetails>(`${apiUrl}/documents/${documentId}`, {
            headers: documentHeaders
        })
            .then(response => {
                setDocumentText(response.data.extracted_text);
                setDocumentDetails(response.data);

                // Use CDN URL (content_ref) for viewing if available, otherwise fall back to original URL
                if (response.data.content_ref) {
                    setDocumentUrl(response.data.content_ref);
                    setIsPdf(response.data.is_pdf || false);
                } else if (response.data.url) {
                    setDocumentUrl(response.data.url);
                    setIsPdf(response.data.is_pdf || false);
                } else {
                    setDocumentUrl(null);
                }
                setLoadingDocument(false);
            })
            .catch(error => {
                console.error('Error fetching document:', error);
                setDocumentText('Error loading document. Please try again.');
                setLoadingDocument(false);
            });
    };

    // Handle clearing the selected document
    const clearSelectedDocument = () => {
        if (returnTo) {
            // Navigate back to where the user came from
            navigate(returnTo);
            setReturnTo(null);
        } else {
            // Default behavior - just close the document view
            setSelectedDocumentId(null);
            setDocumentText(null);
            setDocumentUrl(null);
            setDocumentTitle('');
            // Re-fetch report sources when returning from document view
            if (authLoaded) {
                fetchReportSources();
            }
        }
    };

    // Handle URL parameter for auto-opening document
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const docId = urlParams.get('doc');
        const returnToParam = urlParams.get('returnTo');
        
        // Store returnTo parameter
        if (returnToParam) {
            setReturnTo(decodeURIComponent(returnToParam));
        }
        
        if (docId && authLoaded && !loading) {
            console.log('Auto-opening document with ID:', docId);
            handleViewDocument(docId);
            // Clean up URL parameters
            const newUrl = window.location.pathname;
            window.history.replaceState({}, '', newUrl);
        }
    }, [location.search, authLoaded, loading]);

    // Group search results by document
    const groupResultsByDocument = () => {
        const groupedResults: Record<string, { source?: ReportSource, chunks: SearchResult[] }> = {};
        searchResults.forEach(result => {
            // Get document ID from either source._id or entity.document_id
            const documentId = result.source?._id || result.entity.document_id;
            if (!documentId) return;

            if (!groupedResults[documentId]) {
                // Find source info - either from the result.source or from reportSources
                const source = result.source ||
                    reportSources.find(source => source._id === documentId);

                groupedResults[documentId] = {
                    source: source as ReportSource,
                    chunks: []
                };
            }
            groupedResults[documentId].chunks.push(result);
        });
        return groupedResults;
    };

    // Create a list of all documents from search results
    const getAllDocumentsFromResults = () => {
        const documents = [] as Array<{ _id: string, name?: string, filename?: string }>;

        // First collect documents from search results
        searchResults.forEach(result => {
            const documentId = result.source?._id || result.entity.document_id;
            if (!documentId) return;

            // Skip if we already have this document
            if (documents.some(doc => doc._id === documentId)) return;

            documents.push({
                _id: documentId,
                name: result.source?.name,
                filename: result.source?.filename
            });
        });

        // Also add directly from report sources if not already added
        reportSources.forEach(source => {
            if (!documents.some(doc => doc._id === source._id)) {
                documents.push({
                    _id: source._id,
                    name: source.name,
                    filename: source.filename
                });
            }
        });

        return documents;
    };

    // Handle selecting a document from the sidebar
    const handleSelectDocumentFromSidebar = (docId: string) => {
        handleViewDocument(docId);
    };

    return (
        <div className="container">
            {/* Always show search input and document viewer, regardless of loading state */}
            {selectedDocumentId && (
                <ReportSourceDetailView
                    documentId={selectedDocumentId}
                    documentTitle={documentTitle}
                    documentDetails={documentDetails || undefined}
                    documentText={documentText || ''}
                    documentUrl={documentUrl || ''}
                    isPdf={isPdf}
                    documentChunks={documentChunks}
                    loadingDocument={loadingDocument}
                    clearSelectedDocument={clearSelectedDocument}
                    allDocuments={getAllDocumentsFromResults()}
                    onSelectDocument={handleSelectDocumentFromSidebar}
                />
            )}

            {/* Only show main content when no document is selected */}
            {!selectedDocumentId && (
                <>
                    {/* Unified Search Input */}
                    <div className="search-container" style={{ marginBottom: '20px' }}>
                        <div className="search-input-group">
                            <input
                                type="text"
                                placeholder="Search by title, filename, or ObjectId..."
                                className="search-input"
                                value={searchInput}
                                onChange={(e) => handleSearchChange(e.target.value)}
                                style={{
                                    width: '400px',
                                    padding: '8px 12px',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '4px',
                                    fontSize: '14px'
                                }}
                            />
                            {searchLoading && (
                                <div className="search-spinner-small" style={{
                                    marginLeft: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    color: '#666',
                                    fontSize: '12px'
                                }}>
                                    <i className="fas fa-spinner fa-spin" style={{
                                        fontSize: '12px',
                                        color: '#0366d6'
                                    }}></i>
                                </div>
                            )}
                            {searchQuery && (
                                <button
                                    onClick={handleClearSearch}
                                    className="clear-search-button"
                                    style={{
                                        marginLeft: '8px',
                                        padding: '8px 12px',
                                        backgroundColor: '#6c757d',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '14px'
                                    }}
                                >
                                    <i className="fas fa-times"></i> Clear
                                </button>
                            )}
                        </div>
                        {searchQuery && (
                            <div className="search-info" style={{
                                marginTop: '8px',
                                fontSize: '13px',
                                color: '#666'
                            }}>
                                Searching for: "{searchQuery}"
                                {searchLoading && (
                                    <span style={{ marginLeft: '8px', fontStyle: 'italic' }}>
                                        (updating results...)
                                    </span>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Initial loading indicator */}
                    {loading && !selectedDocumentId && (
                        <Loader text="Loading report sources..." />
                    )}
                    {filteredSources.length > 0 ? (
                        <div className="table-container">
                            {/* Search results section remains the same */}
                            {searchResults.length > 0 && (
                                <div className="search-results-summary">
                                    <p>Found {searchResults.length} search results</p>
                                    <div className="search-results-list">
                                        {Object.entries(groupResultsByDocument()).map(([documentId, data]) => (
                                            <div key={documentId} className="search-result-item" onClick={() => handleViewDocument(documentId)}>
                                                <div className="search-result-metadata">
                                                    <p><strong>Document:</strong> {data.source?.name || data.source?.filename || 'Unknown'}</p>
                                                    <p><strong>Chunks Found:</strong> {data.chunks.length}</p>
                                                    <p><strong>Document ID:</strong> {documentId}</p>

                                                    {data.source?.url && (
                                                        <a
                                                            href={data.source.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="source-link"
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            View Source
                                                        </a>
                                                    )}
                                                    <button
                                                        className="view-document-button secondary-button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleViewDocument(documentId);
                                                        }}
                                                    >
                                                        <i className="fas fa-eye"></i> View
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            
                            <div className="report-sources-list">
                                <table style={{ opacity: searchLoading ? 0.6 : 1 }}>
                                    <thead>
                                    <tr>
                                        <th>Source URL</th>
                                        <th>Title / Filename</th>
                                        <th>Updated</th>
                                        <th></th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {filteredSources.map(source => (
                                        <tr key={source._id}>
                                            <td className="url-cell">
                                                {source.url ? (
                                                    <a
                                                        href={source.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="source-url-link"
                                                        title={source.url} // Show full URL on hover
                                                    >
                                                        <span className="truncate-text">{source.url}</span>
                                                    </a>
                                                ) : '-'}
                                            </td>
                                            <td className="filename-cell">
                                                {source.metadata?.title || source.filename || '-'}
                                                {source.metadata?.title && source.metadata_validation_details?.title?.confidence && (
                                                    <span 
                                                        className="confidence-badge" 
                                                        title={`Confidence: ${(source.metadata_validation_details.title.confidence * 100).toFixed(1)}%`}
                                                        style={{
                                                            marginLeft: '8px',
                                                            fontSize: '0.8em',
                                                            backgroundColor: source.metadata_validation_details.title.confidence >= 0.9 ? '#28a745' : source.metadata_validation_details.title.confidence >= 0.7 ? '#ffc107' : '#dc3545',
                                                            color: 'white',
                                                            padding: '2px 6px',
                                                            borderRadius: '12px',
                                                            fontWeight: 'bold'
                                                        }}
                                                    >
                                                        {(source.metadata_validation_details.title.confidence * 100).toFixed(0)}%
                                                    </span>
                                                )}
                                            </td>
                                            <td className="date-cell">{source.update_timestamp ? new Date(source.update_timestamp).toLocaleDateString() : '-'}</td>
                                            <td>
                                                <button
                                                    className="view-document-button secondary-button"
                                                    onClick={() => handleViewDocument(source._id)}
                                                >
                                                    <i className="fas fa-eye"></i> View
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>

                                {/* Pagination Information */}
                                {pagination && (
                                    <div className="pagination-info">
                                        Showing {filteredSources.length} of {pagination.totalDocuments} sources |
                                        Page {pagination.currentPage} of {pagination.totalPages}
                                    </div>
                                )}

                                {/* Pagination Controls */}
                                {pagination && pagination.totalPages > 1 && (
                                    <div className="pagination-container">
                                        <div className="pagination-info">
                                            Showing {filteredSources.length} of {pagination.totalDocuments} report sources
                                        </div>
                                        <div className="pagination-controls">
                                            <button
                                                onClick={() => handlePageChange(1)}
                                                disabled={!pagination.hasPreviousPage}
                                                className="pagination-button"
                                            >
                                                <i className="fas fa-angle-double-left"></i>
                                            </button>

                                            <button
                                                onClick={() => handlePageChange(currentPage - 1)}
                                                disabled={!pagination.hasPreviousPage}
                                                className="pagination-button"
                                            >
                                                <i className="fas fa-angle-left"></i>
                                            </button>

                                            <span className="pagination-page-info">
                                                Page {pagination.currentPage} of {pagination.totalPages}
                                            </span>

                                            <button
                                                onClick={() => handlePageChange(currentPage + 1)}
                                                disabled={!pagination.hasNextPage}
                                                className="pagination-button"
                                            >
                                                <i className="fas fa-angle-right"></i>
                                            </button>

                                            <button
                                                onClick={() => handlePageChange(pagination.totalPages)}
                                                disabled={!pagination.hasNextPage}
                                                className="pagination-button"
                                            >
                                                <i className="fas fa-angle-double-right"></i>
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="no-results">No report sources available. Please check back later.</div>
                    )}
                </>
            )}

        </div>
    );
};

export default ReportSourcesView;
